'use client';

// eslint-disable-next-line no-restricted-imports
import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { bloomreachUrl } from '@/globals/config/bloomreach.config';
import { ErrorBoundary } from '@/globals/errorBoundary/ErrorBoundary';
import { translateInline } from '@/globals/translate/translateInline';
import { MarkupError } from '@/sections/markup/helpers/MarkupError';
import { origin } from '@/globals/config/origin.config';
type DOPImageProps = Omit<React.ComponentProps<typeof Image>, 'height' | 'width'> & {
  height: number | `${number}`;
  width: number | `${number}`;
};
const bloomreachLoader = ({
  src,
  width,
  quality
}: {
  src: string;
  width: number;
  quality?: number;
}) => {
  const qualityParam = quality != null ? `,quality=${quality}` : '';
  const imageUrl = new URL(src);
  const imagePathname = imageUrl.pathname;
  imageUrl.pathname = `/cdn-cgi/image/format=auto,width=${width}${qualityParam}${imagePathname}`;
  return imageUrl.toString();
};

/**
 * Wraps a NextJS image in a error boundary to prevent a page crash when required height/width are not provided
 */
export const DOPImage = t0 => {
  const $ = _c(10);
  let alt;
  let imageProps;
  let src;
  if ($[0] !== t0) {
    ({
      alt,
      src,
      ...imageProps
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = imageProps;
    $[3] = src;
  } else {
    alt = $[1];
    imageProps = $[2];
    src = $[3];
  }
  const bloomreachOrigin = new URL(bloomreachUrl).origin;
  const srcOrigin = typeof src === "string" ? new URL(src, origin).origin : null;
  const isBloomreachUrl = srcOrigin === bloomreachOrigin;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MarkupError>{translateInline({
        en: "This image could not be loaded (contact a developer)",
        nl: "Deze afbeelding kon niet geladen worden (neem contact op met een ontwikkelaar)"
      })}</MarkupError>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = isBloomreachUrl ? bloomreachLoader : undefined;
  let t3;
  if ($[5] !== alt || $[6] !== imageProps || $[7] !== src || $[8] !== t2) {
    t3 = <ErrorBoundary fallback={t1}><Image alt={alt} src={src} {...imageProps} loader={t2} /></ErrorBoundary>;
    $[5] = alt;
    $[6] = imageProps;
    $[7] = src;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};